import { Button, useOverlayState } from '@heroui/react';
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import classNames from 'classnames';
import {
  AlarmClockIcon,
  ArrowDownIcon,
  ArrowUpRightIcon,
  BellIcon,
  GiftIcon,
  HeartIcon,
} from 'lucide-react';
import { useEffect } from 'react';

import { BladeCard } from 'src/components/blade-card';
import { Container } from 'src/components/container';
import { Countdown } from 'src/components/countdown';
import { FadeIn } from 'src/components/fade-in';
import { ModalBankDetails } from 'src/components/modal-bank-details';
import { scrollToElement } from 'src/utils/scroll-to-section';

export const Route = createFileRoute('/')({
  component: Page,
});

function Page() {
  const overlayState = useOverlayState({ defaultOpen: false });
  const navigate = useNavigate();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      scrollToElement(hash.replace('#', ''), false);
    }
  }, [hash]);

  return (
    <div>
      <section
        id="hero"
        className="relative flex min-h-screen flex-col justify-center py-18 lg:py-34"
      >
        <Container className="relative z-10 flex flex-col justify-center gap-6 text-center uppercase">
          <FadeIn>
            <h1 className={classNames('text-7xl leading-tight md:text-8xl xl:text-9xl', 'w-full')}>
              CAMILA & ADRIÁN
            </h1>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="flex w-full flex-col items-center justify-center gap-1 text-center text-xl uppercase md:flex-row md:gap-4">
              <span>Buenos Aires</span>
              <div className="bg-foreground my-4 h-0.25 w-full max-w-6 rotate-90 md:my-0 md:max-w-20 md:rotate-0"></div>
              <span className="flex flex-col items-center gap-1.5 md:flex-row">
                <BellIcon strokeWidth={1.5} />
                <span>Jueves, Abril 9, 2026</span>
              </span>
            </div>
          </FadeIn>
        </Container>

        <Button
          variant="outline"
          size="sm"
          className="absolute right-0 bottom-8 left-0 mx-auto"
          onPress={() => document.getElementById('intro')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <ArrowDownIcon />
        </Button>
      </section>

      <section id="intro" className="bg-[#fff] py-24 text-center md:py-34">
        <Container>
          <FadeIn>
            <div className="flex flex-col items-center gap-8">
              <HeartIcon strokeWidth={1} size={40} className="text-danger" />
              <p className="max-w-[640px] text-xl">
                <span className="font-semibold">¡Nos casamos!</span> Y queremos compartir este día
                tan especial con las personas que más queremos, por eso nos encantaría que nos
                acompañes en nuestro civil y en el brindis que viene después.
              </p>
              <div className="flex flex-col items-center gap-2">
                <p className="text-xl">con amor,</p>
                <h4 className="text-2xl">Camila & Adrián</h4>
              </div>
            </div>
          </FadeIn>
        </Container>
      </section>

      <section className="bg-[#000] py-18 md:py-24">
        <Container>
          <div className="mb-8 flex flex-row items-center justify-center gap-4">
            <AlarmClockIcon strokeWidth={1} size={40} className="shrink-0 text-white" />
          </div>
          <Countdown />
        </Container>
      </section>

      <section id="programa" className="py-18 md:py-34">
        <Container>
          <FadeIn>
            <h2 className="mb-12 text-center text-4xl">Programa</h2>
          </FadeIn>

          <div className="flex w-full flex-col gap-8 md:gap-12 xl:gap-18">
            <FadeIn>
              <BladeCard
                title="Ceremonia Civil"
                description="El primer paso para oficializar nuestro amor!"
                image="/images/registro-civil.webp"
                reversed
                details={[
                  { label: 'Fecha', value: 'Jueves, Abril 9, 2026' },
                  { label: 'Horario', value: '18:00 hrs.' },
                  { label: 'Dirección', value: 'Uruguay 753, CABA' },
                ]}
              />
            </FadeIn>

            <FadeIn>
              <BladeCard
                title="Brindis"
                description={
                  <>
                    La celebración posterior será en <span className="font-semibold">Milion</span>,
                    ubicado a pocas cuadras del Registro Civil. Si no podés acompañarnos en el
                    civil, ¡te esperamos igual en el brindis!
                  </>
                }
                image="/images/milion.webp"
                details={[
                  { label: 'Fecha', value: 'Jueves, Abril 9, 2026' },
                  { label: 'Horario', value: '19:00 - 23:00 hrs.' },
                  { label: 'Dirección', value: 'Paraná 1048, CABA' },
                  { label: 'Dress code', value: 'Casual' },
                ]}
              />
            </FadeIn>
          </div>
        </Container>
      </section>

      <section id="regalos" className="bg-[#fff] py-18 md:py-34">
        <Container className="flex flex-col items-center gap-8">
          <FadeIn>
            <div className="flex flex-col items-center gap-8">
              <GiftIcon strokeWidth={1} size={40} />
              <p className="max-w-[640px] text-center text-xl">
                <span>
                  No necesitamos nada más que compartir ese día con vos. Pero si aún así querés
                  sumar un gesto,{' '}
                  <span
                    className="hover:text-primary cursor-pointer underline"
                    onClick={overlayState.open}
                  >
                    acá están nuestros datos
                  </span>
                  .
                </span>
              </p>
            </div>
          </FadeIn>

          <ModalBankDetails isOpen={overlayState.isOpen} close={overlayState.close} />
        </Container>
      </section>

      <section id="confirmar" className="bg-[#f2f2f0] py-22 md:py-28">
        <Container>
          <FadeIn>
            <div className="flex flex-col items-center gap-8 md:gap-12">
              <h2 className="text-center text-4xl leading-12 text-balance md:text-6xl md:leading-tight xl:text-7xl">
                Tu presencia significaría mucho para nosotros.
              </h2>
              <Button variant="outline" onClick={() => navigate({ to: '/form' })}>
                Confirmar asistencia
                <ArrowUpRightIcon />
              </Button>
            </div>
          </FadeIn>
        </Container>
      </section>
    </div>
  );
}
